/**
 * E002-T018: Process Tab Component
 * E002-T036: Trigger System Config
 * E002-T037: Batching & Error Handling Config
 * E002-T047: Send as Array Toggle
 *
 * Displays workflow selection and configuration.
 */

import { Menu, MenuItem, notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { ViewIcon } from '@blocksuite/icons/rc';
import { cssVarV2 } from '@streamlain/theme/v2';
import { useService, useServiceOptional } from '@toeverything/infra';
import type { FC } from 'react';
import { useCallback, useEffect } from 'react';

import { WorkspaceDialogService } from '../../dialogs';
import { EditorService } from '../../editor';
import { WorkspaceService } from '../../workspace';
import { useAiKeyCheck } from '../hooks/use-ai-key-check';
import type {
  AutomationConfig,
  InputMapping,
  OutputMapping,
} from '../services/automation';
import { AutomationConfigApiService } from '../services/automation-config-api';
import {
  type BatchConfig,
  normalizeBatchConfig,
  type OnFailureMode,
} from '../services/execution';
import type { WorkflowInfo } from '../services/workflow';
import { WorkflowService } from '../services/workflow';
import { TutorialRegistry } from '../tutorial/services/tutorial-registry';
import * as styles from './automation-panel.css';
import { CollapsibleSection } from './components/collapsible-section';
import { SectionControlRow, SectionNotice } from './components/section-layout';
import { WorkflowSelector } from './components/workflow-selector';
import { type MappingCleanupResult } from './workflow-mapping-diff';
import {
  executeWorkflowSwitch,
  type WorkflowSwitchCommitMode,
} from './workflow-switch-handler';

interface ProcessTabProps {
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** sl-kpg5.27: Block ID for AI key check */
  blockId?: string;
}

export const ProcessTab: FC<ProcessTabProps> = ({
  config,
  onUpdate,
  blockId,
}) => {
  const t = useI18n();

  // E018-S005-T001: Get workflow service for schema loading
  const workflowService = useService(WorkflowService);
  const editor = useServiceOptional(EditorService)?.editor;

  // sl-kpg5.27 + sl-xtes.2: AI Key availability check (shared hook)
  const configApiService = useService(AutomationConfigApiService);
  const workspace = useService(WorkspaceService).workspace;
  const workspaceDialogService = useService(WorkspaceDialogService);
  const missingAiKeys = useAiKeyCheck(
    configApiService,
    blockId,
    workspace?.id,
    config.workflowId
  );

  // Get current batch config or defaults
  const batchConfig = normalizeBatchConfig(config.batchConfig);

  // Local-first canvas projection. This request is tied to an explicit
  // block-scoped sidebar interaction, never to canvas rendering. Persisting
  // the source ID also makes a legitimately empty workflow cacheable.
  useEffect(() => {
    const workflowId = config.workflowId;
    if (!workflowId || config.workflowStepsSourceId === workflowId) return;

    let cancelled = false;
    void workflowService
      .loadWorkflowStepSummaries(workflowId)
      .then(workflowSteps => {
        if (cancelled) return;
        onUpdate({ workflowSteps, workflowStepsSourceId: workflowId });
      })
      .catch(error => {
        console.error(
          '[ProcessTab] Failed to cache workflow steps on automation block:',
          error
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    config.workflowId,
    config.workflowStepsSourceId,
    onUpdate,
    workflowService,
  ]);

  // sl-8fwi.4: Workflow switch — single entry point for both "pick a
  // different workflow" AND "clear the current workflow". The actual
  // pipeline (snapshot old → load new → diff → commit) lives in the
  // pure helper `executeWorkflowSwitch` so it is unit-testable without
  // mounting the sidebar. This callback only injects the React-bound
  // collaborators (notify, onUpdate, t, workflowService).
  //
  // Pre-fix this was a 150-line useCallback closure with three
  // separate Phase 4b audit findings: #60 (KEEP-vs-CLEAR drift on
  // schema load failure), #61 (WorkflowSelector.onClear bypass), #63
  // (test only exercised the inner pure helper, never the integration).
  const handleWorkflowChange = useCallback(
    (workflow: WorkflowInfo | null) => {
      const oldSchema = workflowService.selectedWorkflowSchema$.value;
      const currentInputs = (config.inputMappings ?? []) as InputMapping[];
      const currentOutputs = (config.outputMappings ?? []) as OutputMapping[];

      const commit = (
        cleanup: MappingCleanupResult,
        mode: WorkflowSwitchCommitMode,
        committedWorkflow: WorkflowInfo | null
      ) => {
        onUpdate({
          workflowId: committedWorkflow?.id ?? (undefined as unknown as string),
          workflowName:
            committedWorkflow?.name ?? (undefined as unknown as string),
          workflowSteps: [],
          workflowStepsSourceId: null,
          webhookUrlOverride: committedWorkflow?.webhookUrl || null,
          inputMappings: cleanup.cleanInputMappings,
          outputMappings: cleanup.cleanOutputMappings,
          inputTables: config.inputTables ?? [],
          outputTables: config.outputTables ?? [],
        });

        const removed =
          cleanup.removed.inputs.length + cleanup.removed.outputs.length;

        if (mode === 'schema-load-failed') {
          // sl-8fwi.4 (#60): mappings were KEPT, not cleared. Tell the
          // user via a warning toast so they understand the partial
          // state and can recover manually.
          notify.warning({
            title:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.schema-load-failed.title'
              ](),
            message:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.schema-load-failed.message'
              ](),
          });
          return;
        }

        if (removed === 0) {
          return;
        }
        if (removed === 1) {
          notify.success({
            title:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.removed.title'
              ](),
            message:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.removed.one'
              ](),
          });
        } else {
          notify.success({
            title:
              t[
                'com.streamlain.streamlain.workflow.mapping-cleanup.removed.title'
              ](),
            message: t[
              'com.streamlain.streamlain.workflow.mapping-cleanup.removed.many'
            ]({ count: String(removed) }),
          });
        }
      };

      void executeWorkflowSwitch({
        workflow,
        oldSchema,
        currentInputs,
        currentOutputs,
        loadSchema: id => workflowService.loadWorkflowSchema(id),
        commit,
      });
    },
    [
      config.inputMappings,
      config.outputMappings,
      config.inputTables,
      config.outputTables,
      onUpdate,
      t,
      workflowService,
    ]
  );

  const handleBatchConfigChange = useCallback(
    (updates: Partial<BatchConfig>) => {
      onUpdate({
        batchConfig: normalizeBatchConfig({ ...batchConfig, ...updates }),
      });
    },
    [onUpdate, batchConfig]
  );

  const handleRetryToggle = useCallback(() => {
    handleBatchConfigChange({
      retry: { ...batchConfig.retry, enabled: !batchConfig.retry.enabled },
    });
  }, [handleBatchConfigChange, batchConfig.retry]);

  const handleOnFailureChange = useCallback(
    (mode: OnFailureMode) => {
      handleBatchConfigChange({ onFailure: mode });
    },
    [handleBatchConfigChange]
  );

  // T048: Removed handleMaxRowsPerRunChange and handleSendAsArrayToggle
  // Batching is now controlled via Input Tab "Rows per call"

  // NOTE: Trigger config (type, schedule, webhook) moved to Input Tab

  const registry = useService(TutorialRegistry);

  // On failure options
  const onFailureOptions: { value: OnFailureMode; label: string }[] = [
    {
      value: 'stop',
      label: t['com.streamlain.streamlain.process.failure-stop'](),
    },
    {
      value: 'continue',
      label: t['com.streamlain.streamlain.process.failure-continue'](),
    },
  ];

  return (
    <div className={styles.tabContent}>
      {/* Workflow Section */}
      <div className={styles.section} data-section="workflow-selector">
        <div
          className={styles.sectionTitle}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span>{t['com.streamlain.streamlain.process.workflow']()}</span>
          {config.workflowId && (
            <button
              onClick={() => {
                if (editor && config.workflowId) {
                  editor.openWorkflowFullscreen(
                    config.workflowId,
                    config.workflowName ?? ''
                  );
                } else if (config.workflowId) {
                  window.dispatchEvent(
                    new CustomEvent('streamlain:workflow-fullscreen', {
                      detail: {
                        workflowId: config.workflowId,
                        workflowName: config.workflowName ?? '',
                      },
                    })
                  );
                }
              }}
              title={t['com.streamlain.streamlain.process.view-workflow']()}
              className={styles.viewWorkflowButton}
            >
              <ViewIcon width={14} height={14} />
              {t['com.streamlain.streamlain.common.view']()}
            </button>
          )}
        </div>
        <WorkflowSelector
          value={config.workflowId}
          onChange={handleWorkflowChange}
          // sl-ai-dropdown-race follow-up: Freshly-created workflows skip
          // the smart-diff pipeline and reset all mappings outright. The
          // old workflow's schema is irrelevant to a blank template and
          // any accidental field-id overlap would leave a half-configured
          // sidebar state.
          onCreate={workflow =>
            onUpdate({
              workflowId: workflow.id,
              workflowName: workflow.name,
              workflowSteps: [],
              workflowStepsSourceId: null,
              webhookUrlOverride: workflow.webhookUrl || null,
              inputMappings: [],
              outputMappings: [],
              inputTables: config.inputTables ?? [],
              outputTables: config.outputTables ?? [],
            })
          }
          // sl-8fwi.4 (#61 fix): Route the clear path through the same
          // handleWorkflowChange entry point. Previously this called
          // onUpdate directly with empty mapping arrays, which silently
          // dropped the cleanup audit trail and showed no toast.
          onClear={() => handleWorkflowChange(null)}
          placeholder={t['com.streamlain.streamlain.process.select-workflow']()}
        />
      </div>

      {/* sl-kpg5.27: AI Key Warning Banner */}
      {missingAiKeys.length > 0 && (
        <div
          style={{
            margin: '0 0 8px 0',
            padding: '10px 12px',
            backgroundColor: `color-mix(in srgb, ${cssVarV2('others/warnLabel/yellow')} 8%, transparent)`,
            border: `1px solid color-mix(in srgb, ${cssVarV2('others/warnLabel/yellow')} 20%, transparent)`,
            borderRadius: '8px',
            fontSize: '12px',
            color: cssVarV2('text/primary'),
            lineHeight: '1.4',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            {t['com.streamlain.streamlain.process.missing-ai-keys']()}
          </div>
          <div style={{ color: cssVarV2('text/secondary') }}>
            {t['com.streamlain.streamlain.process.missing-ai-keys-detail']({
              keys: missingAiKeys.join(', '),
            })}
          </div>
          <div style={{ marginTop: '6px' }}>
            <span
              role="button"
              tabIndex={0}
              onClick={() =>
                workspaceDialogService.open('setting', {
                  activeTab: 'workspace:ai',
                })
              }
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  workspaceDialogService.open('setting', {
                    activeTab: 'workspace:ai',
                  });
                }
              }}
              style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                color: cssVarV2('text/link'),
                fontSize: '12px',
              }}
            >
              {t['com.streamlain.streamlain.execution.open-ai-settings']?.() ??
                t['com.streamlain.streamlain.execution.configure-ai-keys']()}
            </span>
          </div>
        </div>
      )}

      {/* 2. Advanced Section - Execution Settings */}
      <div data-section="advanced">
        <CollapsibleSection
          title={t['com.streamlain.streamlain.common.advanced']()}
          subtitle={t['com.streamlain.streamlain.process.advanced-subtitle']()}
          onInfoClick={() => registry.start('automation:processWorkflow')}
          defaultCollapsed
        >
          {/* Retry Toggle */}
          <SectionControlRow
            label={t['com.streamlain.streamlain.process.retry-on-failure']()}
            coachmarkTarget="retry-toggle"
          >
            <button
              onClick={handleRetryToggle}
              style={{
                width: '44px',
                height: '24px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: batchConfig.retry.enabled
                  ? cssVarV2('button/primary')
                  : cssVarV2('layer/insideBorder/border'),
                position: 'relative',
                transition: 'background-color 0.2s',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: batchConfig.retry.enabled ? '22px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  transition: 'left 0.2s',
                }}
              />
            </button>
          </SectionControlRow>

          {batchConfig.retry.enabled && (
            <SectionNotice>
              {t['com.streamlain.streamlain.process.retry-info']({
                count: String(batchConfig.retry.maxAttempts),
              })}
            </SectionNotice>
          )}

          {/* On Failure - Custom Menu */}
          <SectionControlRow
            label={t['com.streamlain.streamlain.process.on-failure']()}
            coachmarkTarget="on-failure-menu"
          >
            <Menu
              items={
                <>
                  {onFailureOptions.map(option => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleOnFailureChange(option.value)}
                      selected={batchConfig.onFailure === option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </>
              }
            >
              <button
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 10px',
                  fontSize: '12px',
                  border: `1px solid ${cssVarV2('layer/insideBorder/border')}`,
                  borderRadius: '6px',
                  backgroundColor: cssVarV2('layer/background/primary'),
                  color: cssVarV2('text/primary'),
                  cursor: 'pointer',
                }}
              >
                {onFailureOptions.find(o => o.value === batchConfig.onFailure)
                  ?.label ||
                  t['com.streamlain.streamlain.process.failure-stop']()}
                <span
                  style={{
                    color: cssVarV2('text/tertiary'),
                    fontSize: '10px',
                  }}
                >
                  ▼
                </span>
              </button>
            </Menu>
          </SectionControlRow>
        </CollapsibleSection>
      </div>
    </div>
  );
};
